/**
 * Dataset 별 에피소드 목록 소스. 두 모드를 한 훅으로 제공한다.
 *
 * - **page-replace(기본)**: prev/next 로 한 페이지씩 교체한다(누적하지 않는다). Files 모드
 *   page-group 이 쓴다. 에피소드가 수백~수천 개이므로 한 번에 받지 않는다(rules §7).
 * - **accumulate(`accumulate: true`)**: 페이지를 concat 해 무한 스크롤(`loadMore`/`hasMore`)로
 *   계속 받는다. Episodes 모드 사이드바가 쓴다. `refresh` 로 불러온 범위를 제자리에서 다시 받는다.
 *
 * raw → EpisodeSummary projection 은 seam(`FileBrowserSource.listEpisodes`) 이 소유한다.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { EPISODE_LIST_FILTER_KEYS, type EpisodeListFilters, type EpisodeSummary } from "@/api";
import { createLogger } from "@/logging";
import { resolveLoadErrorMessage, type LoadState } from "./loadState";
import type { FileBrowserSource } from "./fileBrowserSource";

const logger = createLogger("components/dataset/episodes");
const DEFAULT_EPISODE_PAGE_SIZE = 20;
// BE PageQuery 상한(api/pagination.py PAGE_SIZE_MAX)과 같은 값이다. 제자리 재조회는 불러온 범위를
// 이 크기 이하의 요청으로 나눠 받는다. 누적 목록이 수백 행이어도 요청 수가 적다.
const REFRESH_CHUNK_MAX = 100;

export type UseEpisodePageOptions = {
  pageSize?: number;
  // true 이면 페이지를 교체하지 않고 누적(concat)한다. 무한 스크롤(hasMore/loadMore)에 쓴다.
  accumulate?: boolean;
  // 서버 리뷰 필터. 바뀌면 page 0 부터 다시 받는다. 서버가 거르므로 total 도 함께 줄어든다.
  filters?: EpisodeListFilters;
};

export type UseEpisodePageResult = {
  // page-replace: 현재 페이지 항목. accumulate: 지금까지 누적된 항목. status 는 첫 로드/에러가 정한다.
  state: LoadState<EpisodeSummary[]>;
  // 0-based 현재 페이지. accumulate 에서는 마지막으로 로드한 페이지다.
  page: number;
  total: number;
  pageSize: number;
  totalPages: number;
  // page-replace: 현재 페이지의 1-based 끝 인덱스("20 / 100"). accumulate: 누적 로드 수.
  rangeEnd: number;
  // 아래 값은 accumulate 모드에서만 의미가 있다(page-replace 는 hasMore=false, loadMore=no-op).
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
  goPrev: () => void;
  goNext: () => void;
  // 에러가 나면 재시도한다(현재 페이지를 다시 받는다).
  reload: () => void;
  /**
   * accumulate 모드에서 지금까지 불러온 범위를 제자리에서 다시 받는다. 받은 행을 돌려준다.
   *
   * 처음부터 다시 받지 않는다. 그러면 누적 목록과 스크롤이 사라지고 선택한 행을 잃는다. 로딩
   * 상태로 바꾸지 않고, 응답이 오면 목록과 `total` 을 한 번에 바꾼다. 적용하지 못하면 null 이다
   * (첫 적재 전·page-replace 모드·재조회 중 필터 변경·요청 실패).
   *
   * `extend` 이면 한 페이지를 더 받는다. 마지막으로 불러온 행 다음을 찾을 때 쓴다.
   */
  refresh: (options?: EpisodeRefreshOptions) => Promise<readonly EpisodeSummary[] | null>;
};

export type EpisodeRefreshOptions = { extend?: boolean };

export function useEpisodePage(
  source: FileBrowserSource,
  datasetUuid: string,
  options: UseEpisodePageOptions = {},
): UseEpisodePageResult {
  const { pageSize = DEFAULT_EPISODE_PAGE_SIZE, accumulate = false, filters } = options;

  const [page, setPage] = useState(0);
  const [state, setState] = useState<LoadState<EpisodeSummary[]>>({ status: "idle" });
  const [total, setTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  // accumulate 모드에서 이전 페이지 항목을 이어 붙일 최신 스냅샷. effect 를 다시 실행하지 않고 참조한다.
  const itemsRef = useRef<EpisodeSummary[]>([]);
  // loadMore 중복 호출 가드. ListView 의 IntersectionObserver 는 loadingMore 중 호출을 막지 않는다.
  const loadingMoreRef = useRef(false);
  // 직전에 처리한 reset 입력. source 는 객체 identity 로 비교한다. source 는 바뀌지 않는 모듈
  // 상수이므로 이 비교로 충분하다. 문자열 키에 넣지 않는다. 어느 값이든 바뀌면 목록을 처음부터 다시 받는다.
  const resetRef = useRef<{ source: FileBrowserSource; uuid: string; key: string } | null>(null);
  // 목록 세대. reset 과 제자리 재조회를 적용할 때 늘린다. 세대가 바뀌기 전에 시작한 응답은 버린다.
  // 그래서 재조회가 이미 담은 페이지를 늦게 온 추가 페이지 응답이 한 번 더 붙이지 않는다.
  const generationRef = useRef(0);
  // 제자리 재조회를 한 줄로 세운다. 겹쳐 부르면 앞 호출이 끝난 뒤 다음 호출이 시작한다. 그래서
  // 각 호출이 자기 결과를 받고, 마지막에 시작한 호출의 결과가 화면에 남는다.
  const refreshChainRef = useRef<Promise<unknown>>(Promise.resolve());
  // 재조회 중에는 추가 페이지를 요청하지 않는다. 재조회가 끝나면 목록 길이가 바뀔 수 있다.
  const refreshingRef = useRef(false);
  // 마지막으로 요청한 페이지. 페이지를 옮기는 곳은 모두 `movePage` 로 이 값을 먼저 바꾼다. state
  // `page` 는 그리기용 거울이고, 렌더에서 이 값을 맞추지 않는다. 추가 요청 직후의 재조회는 다시
  // 렌더하기 전에 실행될 수 있고, 급한 렌더는 아직 반영되지 않은 page 로 이 값을 되돌린다. 그러면
  // 재조회가 방금 요청한 페이지를 빼고 받아 그 행을 잃는다.
  const pageRef = useRef(page);
  const movePage = useCallback((next: number) => {
    pageRef.current = next;
    setPage(next);
  }, []);
  const statusRef = useRef(state.status);
  statusRef.current = state.status;
  // `refresh({ extend })` 가 이미 받은 페이지 번호. page-effect 는 이 페이지를 다시 받지 않는다.
  const prefetchedPageRef = useRef<number | null>(null);

  // 필터는 값으로 비교한다. 그래서 호출자가 렌더마다 새 객체를 전달해도 된다. identity 로 비교하면
  // effect 가 렌더마다 다시 실행되어 재조회가 끝나지 않는다. 요청에 넣을 실제 객체는 ref 로 읽는다.
  const filterKey = EPISODE_LIST_FILTER_KEYS.map((key) => filters?.[key] ?? "").join("|");
  const filtersRef = useRef<EpisodeListFilters | undefined>(filters);
  filtersRef.current = filters;

  // 모드, pageSize, 서버 필터의 조합. 바뀌면 page 0 부터 다시 받는다.
  const resetKey = `${accumulate}|${pageSize}|${filterKey}`;

  useEffect(() => {
    if (!datasetUuid) {
      resetRef.current = { source, uuid: datasetUuid, key: resetKey };
      generationRef.current += 1;
      itemsRef.current = [];
      setState({ status: "idle" });
      setTotal(0);
      movePage(0);
      return;
    }
    // reset(파라미터 교체)이면 누적과 가드를 비우고 page 0 부터 받는다. page 가 아직 0 이 아니면
    // 이번 실행은 건너뛰고, movePage(0) 이 일으키는 재렌더에서 로드한다. 그래서 이전 page 값으로
    // 잘못된 페이지를 받지 않는다.
    const previous = resetRef.current;
    // source/resource 가 바뀌면 이전 목록은 다른 데이터이므로 state 와 total 을 즉시 비운다.
    // 같은 소스의 필터 reset 은 재조회 동안 기존 표시(총개수 등)를 유지한다.
    const sourceOrResourceChanged =
      previous !== null && (previous.source !== source || previous.uuid !== datasetUuid);
    const isReset = previous === null || sourceOrResourceChanged || previous.key !== resetKey;
    if (isReset) {
      resetRef.current = { source, uuid: datasetUuid, key: resetKey };
      generationRef.current += 1;
      itemsRef.current = [];
      loadingMoreRef.current = false;
      if (sourceOrResourceChanged) {
        setState({ status: "loading" });
        setTotal(0);
        setLoadingMore(false);
      }
      if (page !== 0) {
        movePage(0);
        return;
      }
    }
    if (!isReset && prefetchedPageRef.current === page) {
      prefetchedPageRef.current = null;
      return;
    }
    prefetchedPageRef.current = null;
    let cancelled = false;
    const generation = generationRef.current;
    // accumulate 에서 page>0 은 추가 로딩(목록 유지, loadingMore)이다. 그 외는 첫 로딩(전체 스피너)이다.
    const appending = accumulate && page > 0;
    if (appending) setLoadingMore(true);
    else setState({ status: "loading" });
    void source
      .listEpisodes(datasetUuid, { page: page + 1, size: pageSize, ...filtersRef.current })
      .then((resp) => {
        if (cancelled || generation !== generationRef.current) return;
        const merged = appending ? appendNew(itemsRef.current, resp.items) : resp.items;
        itemsRef.current = merged;
        setState({ status: "success", data: merged });
        setTotal(resp.total);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logger.error("listEpisodes failed", error);
        // 추가 페이지가 실패하면 누적 목록을 유지한다. 첫 페이지 실패만 에러 상태로 표시한다.
        if (!appending) setState({ status: "error", message: resolveLoadErrorMessage(error) });
      })
      .finally(() => {
        if (cancelled) return;
        if (appending) setLoadingMore(false);
        loadingMoreRef.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [source, datasetUuid, page, pageSize, accumulate, resetKey, filterKey, reloadToken, movePage]);

  const loadedCount = state.status === "success" ? state.data.length : 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeEnd = accumulate ? loadedCount : Math.min((page + 1) * pageSize, total);
  const hasMore = accumulate && state.status === "success" && loadedCount < total;

  const goPrev = useCallback(() => movePage(Math.max(0, pageRef.current - 1)), [movePage]);
  const goNext = useCallback(() => movePage(pageRef.current + 1), [movePage]);
  // 다음 페이지를 누적한다. 이미 로딩 중이거나 재조회 중이면 무시한다(중복 호출 방지). 실제 적재는
  // page-effect 가 한다.
  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || refreshingRef.current) return;
    loadingMoreRef.current = true;
    movePage(pageRef.current + 1);
  }, [movePage]);
  const reload = useCallback(() => setReloadToken((current) => current + 1), []);

  const refresh = useCallback(
    (options: EpisodeRefreshOptions = {}): Promise<readonly EpisodeSummary[] | null> => {
      const run = refreshChainRef.current.then(async () => {
        if (!accumulate || !datasetUuid || statusRef.current !== "success") return null;
        const generation = generationRef.current;
        const pages = pageRef.current + 1 + (options.extend === true ? 1 : 0);
        const want = pages * pageSize;
        const chunk = Math.max(pageSize, Math.floor(REFRESH_CHUNK_MAX / pageSize) * pageSize);
        refreshingRef.current = true;
        try {
          const requests = [];
          for (let offset = 0; offset < want; offset += chunk) {
            requests.push(
              source.listEpisodes(datasetUuid, {
                page: offset / chunk + 1,
                size: chunk,
                ...filtersRef.current,
              }),
            );
          }
          const responses = await Promise.all(requests);
          if (generation !== generationRef.current) return null;
          const rows = responses.flatMap((resp) => resp.items).slice(0, want);
          generationRef.current += 1;
          itemsRef.current = rows;
          setState({ status: "success", data: rows });
          setTotal(responses[0]?.total ?? 0);
          // 오는 중이던 추가 페이지는 세대가 바뀌어 적용되지 않는다. 그 행은 이 재조회에 들어 있다.
          // extend 가 페이지를 앞당기면 그 요청의 effect 가 정리되어 표시를 풀 곳이 없다. 여기서 푼다.
          loadingMoreRef.current = false;
          setLoadingMore(false);
          // 더 받은 페이지까지 불러온 것으로 센다. 다음 추가 요청은 그 다음 페이지부터다.
          if (pages - 1 > pageRef.current) {
            prefetchedPageRef.current = pages - 1;
            movePage(pages - 1);
          }
          return rows;
        } catch (error: unknown) {
          logger.error("listEpisodes refresh failed", error);
          return null;
        } finally {
          refreshingRef.current = false;
        }
      });
      refreshChainRef.current = run;
      return run;
    },
    [source, datasetUuid, pageSize, accumulate, movePage],
  );

  return {
    state,
    page,
    total,
    pageSize,
    totalPages,
    rangeEnd,
    hasMore,
    loadingMore,
    loadMore,
    goPrev,
    goNext,
    reload,
    refresh,
  };
}

// 누적 목록 뒤에 새 페이지를 붙인다. 이미 있는 행은 다시 붙이지 않는다. 재조회와 추가 페이지가
// 겹쳐도 한 행이 두 번 보이지 않는다.
function appendNew(
  current: readonly EpisodeSummary[],
  incoming: readonly EpisodeSummary[],
): EpisodeSummary[] {
  const seen = new Set(current.map((episode) => episode.uuid));
  return [...current, ...incoming.filter((episode) => !seen.has(episode.uuid))];
}
